import {useEffect} from 'react';
import {useQuery, useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

import {isAndroid} from '@zzng/common/utils/agent';

import {getCardDetails, getCardGroup, getCardGroupByCardId} from '@/root/apis/cards';
import {useCardGroupStore} from '@/root/stores/cardGroup';
import {processCardDetails, processCardGroup} from '@/root/utils/processor';

export const cardsKeys = {
  all: ['cards'] as const,
  group: (groupCode: string) => [...cardsKeys.all, 'groups', groupCode] as const,
  details: (cardId: string) => [...cardsKeys.all, 'details', cardId] as const,
  groupByCardId: (cardId: string) => [...cardsKeys.all, 'groupByCardId', cardId] as const
};

export const useCardGroup = (groupCode = '') => {
  const {setGroupCode} = useCardGroupStore();

  const {data} = useSuspenseQuery({
    queryKey: cardsKeys.group(groupCode),
    queryFn: async () => {
      setGroupCode(groupCode); // 티아라 로깅을 위해 API 호출 전 저장
      const cardGroup = await getCardGroup(groupCode);
      return processCardGroup(cardGroup);
    },

    // 카드 삭제, 재발급 후 다시 카드 상세로 돌아올 때 refetch 필요 (재발급 웹뷰는 카드 상세 웹뷰 위에 뜬다.)
    staleTime: 2000,
    refetchOnWindowFocus: true
  });

  const queryClient = useQueryClient();

  // AOS에서 카드 재발급 후 refetchOnWindowFocus 옵션이 작동하지 않아 수동으로 처리
  useEffect(() => {
    if (!isAndroid) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({queryKey: cardsKeys.group(groupCode)});
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient, groupCode]);

  return {data};
};

export const useCardDetails = (cardId: string) => {
  const {data: card} = useQuery({
    queryKey: cardsKeys.details(cardId),
    queryFn: async () => {
      const card = await getCardDetails(cardId);
      return processCardDetails(card);
    },

    // NOTE: 카드 상세가 변경될 가능성이 있어 staleTime 지정 (ex. 톡사원증 > 회사정보 관리)
    staleTime: 2000,
    refetchOnWindowFocus: true
  });

  return {card};
};

export const useCardGroupByCardId = (cardId: string) => {
  const {setGroupCode} = useCardGroupStore();

  const {data: cardGroup} = useSuspenseQuery({
    queryKey: cardsKeys.groupByCardId(cardId),
    queryFn: async () => {
      const cardGroup = await getCardGroupByCardId(cardId);
      setGroupCode(cardGroup.group.code);
      return processCardGroup(cardGroup);
    }
  });

  return {cardGroup};
};
